import classNames from "classnames";
import React from "react";

import { tableDataType } from "src/app/components/Table/Table";
import TooltipPortal from "src/app/components/TooltipPortal/TooltipPortal";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import useSortableItem from "src/app/components/VerticalDragAndDropList/useSortableItem";
import { ReactComponent as CircleCheck } from "src/images/circle_check.svg";
import { ReactComponent as CircleEmpty } from "src/images/circle_empty.svg";
import { ReactComponent as GridIcon } from "src/images/grid.svg";
import { ReactComponent as TickBlue } from "src/images/tick-blue.svg";
import { ReactComponent as TickLightGrey } from "src/images/tick-light-grey.svg";
import { GlobalField } from "../GlobalFields";
import { GlobalFieldActionDropdown } from "./GlobalFieldActionDropdown/GlobalFieldActionDropdown";
import {
  isGlobalTaskMetaField,
  MetaFieldIcon,
  renderTooltipContent,
  renderTooltipIcon,
  UsedInTooltip,
} from "../utils";

interface DraggableGlobalFieldItemProps {
  option: GlobalField;
  dataType?: tableDataType;
  isFieldSelected?: boolean;
  enableFieldManaging?: boolean;
  handleClick: (option: GlobalField) => void;
}

export default function DraggableGlobalFieldItem({
  option,
  dataType,
  isFieldSelected,
  enableFieldManaging,
  handleClick,
}: DraggableGlobalFieldItemProps) {
  const { attributes, listeners, setNodeRef, style } = useSortableItem(
    option.uuid,
    !enableFieldManaging, // disable drag when not in field managing mode
  );

  const hasAnyUsedInData =
    option.linkedTemplatesCount > 0 ||
    option.linkedProjectsCount > 0 ||
    (isGlobalTaskMetaField(option) && option.showInCreatorDatabase);

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={classNames(
        "global-fields__list-item",
        option.existsInProject && "global-fields__list-item--disabled",
      )}
      onClick={() => handleClick(option)}
      data-qa-field-name={option.name}
    >
      {enableFieldManaging && (
        <span
          className="global-fields__list-item-drag"
          {...attributes}
          {...listeners}
        >
          <GridIcon />
        </span>
      )}
      <div
        className={classNames("global-fields__text", {
          "global-fields__text--task": dataType === tableDataType.Task,
          "global-fields__text--project": dataType === tableDataType.Project,
        })}
      >
        <div className="global-fields__name">
          <span className="global-fields__name-text">{option.name}</span>
          {option.existsInProject && <TickLightGrey />}
          {!option.existsInProject && isFieldSelected && <TickBlue />}
        </div>
        <div className="global-fields__description">{option.description}</div>
      </div>
      {enableFieldManaging && (
        <div className="global-fields__used">
          <TooltipPortal content={<UsedInTooltip field={option} />}>
            {hasAnyUsedInData ? <CircleCheck /> : <CircleEmpty />}
          </TooltipPortal>
        </div>
      )}
      {dataType === tableDataType.Task && (
        <div className="global-fields__applicable-for">
          {option.taskType || (
            <IDHFormattedMessage id="ws_all" defaultMessage="All" />
          )}
        </div>
      )}
      <div className="global-fields__icon">
        <TooltipPortal
          content={renderTooltipContent(option.type, option.valueSource)}
        >
          <MetaFieldIcon
            icon={renderTooltipIcon(option.type, option.valueSource)}
          />
        </TooltipPortal>
      </div>
      {enableFieldManaging && dataType && (
        <div className="global-fields__action">
          <GlobalFieldActionDropdown data={option} dataType={dataType} />
        </div>
      )}
    </li>
  );
}
